import type { ClockTimePrecision, ScheduleItem } from '../types/domain.js'

export interface ClockSample { clientStart: number; clientEnd: number; serverTime: number }
export interface ClockAnchor { serverTime: number; monotonicAt: number; rtt: number; measuredAt: number }

export function bestClockSample(samples: readonly ClockSample[]): ClockAnchor | null {
  const valid = samples.filter(sample => sample.clientEnd >= sample.clientStart && sample.clientEnd - sample.clientStart <= 250)
  if (!valid.length) return null
  const sample = valid.reduce((best, value) => value.clientEnd - value.clientStart < best.clientEnd - best.clientStart ? value : best)
  const rtt = sample.clientEnd - sample.clientStart
  return { serverTime: sample.serverTime + rtt / 2, monotonicAt: sample.clientEnd, rtt, measuredAt: Date.now() }
}

export function serverNow(anchor: ClockAnchor, monotonicNow: number): number {
  return anchor.serverTime + Math.max(0, monotonicNow - anchor.monotonicAt)
}

function precisionDigits(precision: ClockTimePrecision | boolean | undefined): number {
  if (precision === true || precision === 'TENTHS') return 1
  if (precision === 'HUNDREDTHS') return 2
  if (precision === 'MILLISECONDS') return 3
  return 0
}

function formatFraction(milliseconds: number, digits: number): string {
  if (!digits) return ''
  return `.${String(Math.floor(milliseconds / 10 ** (3 - digits))).padStart(digits, '0')}`
}

export function formatClock(ms: number, precision: ClockTimePrecision | boolean = 'SECONDS'): string {
  const date = new Date(ms)
  const base = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date)
  return `${base}${formatFraction(date.getMilliseconds(), precisionDigits(precision))}`
}

export function formatDuration(ms: number, options: { signed?: boolean; tenthsBelowSec?: number; forceHours?: boolean; precision?: ClockTimePrecision } = {}): string {
  const negative = ms < 0
  const absolute = Math.abs(ms)
  const totalSec = Math.floor(absolute / 1000)
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  const sign = options.signed ? (negative ? '−' : '+') : (negative ? '−' : '')
  const whole = hours || options.forceHours
    ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${String(minutes + hours * 60).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  const digits = options.precision
    ? precisionDigits(options.precision)
    : options.tenthsBelowSec != null && absolute <= options.tenthsBelowSec * 1000 ? 1 : 0
  return `${sign}${whole}${formatFraction(absolute % 1000, digits)}`
}

export function itemTitle(item: ScheduleItem | null | undefined): string {
  const liveOverrideTitle = item?.itemType === 'LIVE' && typeof item.overrides?.title === 'string'
    ? item.overrides.title.trim()
    : ''
  return liveOverrideTitle || item?.program?.titleShort || item?.program?.title || item?.liveSource?.name || item?.notes || item?.itemType || '—'
}

export type NextLivePhase = 'NONE' | 'UPCOMING' | 'SOON' | 'IMMINENT' | 'WAITING' | 'LIVE' | 'ENDING_SOON' | 'ENDING_IMMINENT' | 'OVERRUN'

export interface NextLiveState {
  phase: NextLivePhase
  label: string
  counterMs: number | null
  item: ScheduleItem | null
  severity: 'default' | 'warning' | 'danger' | 'live'
  theoreticalEnd: boolean
}

/**
 * Un direct sorti de l'antenne ne pilote plus l'horloge, quoi qu'en dise son
 * horaire. Sans cette borne, la tolérance de 24 h accordée à `liveGoLiveAt`
 * gardait un direct terminé « actif » en phase DÉPASSEMENT jusqu'au lendemain :
 * une fin manuelle anticipée figeait la Clock sur un compteur qui monte.
 */
const LIVE_FINISHED_STATUSES: ReadonlyArray<ScheduleItem['status']> = ['DONE', 'SKIPPED', 'ERROR']

export function nextLiveState(items: readonly ScheduleItem[], nowMs: number, thresholds: { warningSec?: number; dangerSec?: number; endWarningSec?: number; endDangerSec?: number } = {}): NextLiveState {
  const warning = (thresholds.warningSec ?? 600) * 1000
  const danger = (thresholds.dangerSec ?? 120) * 1000
  const endWarning = (thresholds.endWarningSec ?? 300) * 1000
  const endDanger = (thresholds.endDangerSec ?? 60) * 1000
  const lives = items.filter(item => item.itemType === 'LIVE')
  const active = lives.find(item =>
    !LIVE_FINISHED_STATUSES.includes(item.status) && (
      (item.status === 'ON_AIR' && (item.liveStartMode !== 'MANUAL' || Boolean(item.liveGoLiveAt))) ||
      (item.liveGoLiveAt && nowMs >= new Date(item.liveGoLiveAt).getTime() && nowMs < new Date(item.endTime).getTime() + 24 * 3600_000)
    ),
  )
  if (active) {
    const endDelta = new Date(active.endTime).getTime() - nowMs
    const theoreticalEnd = active.liveEndMode === 'MANUAL'
    if (endDelta < 0) return { phase: 'OVERRUN', label: 'DÉPASSEMENT', counterMs: -endDelta, item: active, severity: 'danger', theoreticalEnd }
    if (endDelta <= endDanger) return { phase: 'ENDING_IMMINENT', label: 'FIN IMMINENTE', counterMs: endDelta, item: active, severity: 'danger', theoreticalEnd }
    if (endDelta <= endWarning) return { phase: 'ENDING_SOON', label: 'FIN PROCHE', counterMs: endDelta, item: active, severity: 'warning', theoreticalEnd }
    return { phase: 'LIVE', label: 'EN DIRECT', counterMs: endDelta, item: active, severity: 'live', theoreticalEnd }
  }
  const next = lives.find(item => !LIVE_FINISHED_STATUSES.includes(item.status) && new Date(item.endTime).getTime() > nowMs)
  if (!next) return { phase: 'NONE', label: 'AUCUN DIRECT PRÉVU', counterMs: null, item: null, severity: 'default', theoreticalEnd: false }
  const delta = new Date(next.startTime).getTime() - nowMs
  if (delta <= 0 && next.liveStartMode === 'MANUAL' && !next.liveGoLiveAt) return { phase: 'WAITING', label: 'EN ATTENTE DU TOP', counterMs: -delta, item: next, severity: 'danger', theoreticalEnd: next.liveEndMode === 'MANUAL' }
  if (delta <= danger) return { phase: 'IMMINENT', label: 'TOP IMMINENT', counterMs: Math.max(0, delta), item: next, severity: 'danger', theoreticalEnd: next.liveEndMode === 'MANUAL' }
  if (delta <= warning) return { phase: 'SOON', label: 'BIENTÔT', counterMs: delta, item: next, severity: 'warning', theoreticalEnd: next.liveEndMode === 'MANUAL' }
  return { phase: 'UPCOMING', label: 'À VENIR', counterMs: delta, item: next, severity: 'default', theoreticalEnd: next.liveEndMode === 'MANUAL' }
}

export function isClockProgramme(item: ScheduleItem): boolean {
  return item.itemType === 'PROGRAM' || item.itemType === 'LIVE'
}

export function currentAndNext(items: readonly ScheduleItem[], nowMs: number) {
  const current = items.find(item => item.status === 'ON_AIR') ?? items.find(item => new Date(item.startTime).getTime() <= nowMs && new Date(item.endTime).getTime() > nowMs) ?? null
  const future = items
    .filter(item => new Date(item.startTime).getTime() > nowMs)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
  const next = future[0] ?? null
  const nextProgram = future.find(isClockProgramme) ?? null
  return { current, next, nextProgram }
}
